import { ENV, REGION } from 'src/core/config/APIConstants';
import { getEnv, getRegion } from 'src/utils/Environment';

/**
 * Blanket DUB (EU / eu-west-1) -> ZAZ (eu-south-2) operator redirect on the
 * Midway front door, draining the entire EU-region Newton home to ZAZ.
 *
 * Newton Web is a Katal/React SPA (no server), so — unlike the Lagrange servlet
 * `RegionRedirectFilter` (`com.amazon.lagrangedashboard.filters.RegionRedirectFilter`)
 * that issues an HTTP 302 — this is delivered as a SPA-bootstrap redirect:
 * {@link runRegionRedirect} runs once before React mounts and, for any EU-door
 * page, performs a full `window.location.replace( targetUrl )` to the ZAZ host.
 * The whole session (frontend + backend) moves to ZAZ; no DUB Midway auth or
 * backend call is made.
 *
 * Scope — the Midway door only (`prod.eu.newton.aft.amazon.dev`):
 *  - AFT-X (`newton.<region>.aftx.amazonoperations.app`) is already region-routed
 *    per FC by the FC Menu platform, so this no-ops there to avoid competing.
 *  - Production only — ZAZ does not exist in dev / beta / gamma.
 *  - EU door only — NA / NRT are untouched, and ZAZ itself is skipped (loop-safe).
 *
 * Every EU-door request is redirected regardless of FC: this is a full-region
 * cutover, not a per-FC allowlisted rollout. (An earlier revision gated on a
 * bundled FC allowlist; that was removed in favour of draining all EU traffic.
 * Reintroduce a per-FC / central-table gate here if a gradual cutover is ever
 * needed again.)
 */

/** The DUB -> ZAZ redirect target host (Midway). */
export const ZAZ_REDIRECT_HOST = 'prod.zaz.newton.aft.amazon.dev';

/**
 * Query param appended to the ZAZ target URL so the ZAZ page can tell the
 * operator they were redirected (see `RegionRedirectBanner`). DUB and ZAZ are
 * different origins, so the URL is the only reliable cross-door carrier for
 * this signal. Consumed (stripped) by {@link consumeRedirectMarker} before
 * React mounts, so it never survives into bookmarks, shares, or refreshes.
 */
export const REDIRECT_SOURCE_PARAM = 'redirectedFrom';

/** Marker value identifying the DUB (EU) door as the redirect source. */
export const REDIRECT_SOURCE_VALUE = 'dub';

/** The source region being drained to ZAZ: EU (Dublin / eu-west-1). */
export const DUB_REGION: REGION = REGION.EU;

/** True for the AFT-X front door, which the FC Menu platform region-routes. */
const isAftxHost = ( host: string ): boolean => host.endsWith( '.aftx.amazonoperations.app' );

/**
 * Builds the ZAZ target URL from the configured ZAZ host, preserving the
 * operator's current path, query string, and hash fragment exactly, and
 * appending the {@link REDIRECT_SOURCE_PARAM} marker so the ZAZ page can show
 * a "you have been redirected" banner.
 */
const buildTargetUrl = ( path: string, query?: string, hash?: string ): string =>
{
    const normalizedPath = path.startsWith( '/' ) ? path : `/${path}`;
    const normalizedQuery = query && query.length > 0
        ? ( query.startsWith( '?' ) ? query : `?${query}` )
        : '';
    const marker = `${REDIRECT_SOURCE_PARAM}=${REDIRECT_SOURCE_VALUE}`;
    const queryWithMarker = normalizedQuery.length > 0
        ? `${normalizedQuery}&${marker}`
        : `?${marker}`;
    const normalizedHash = hash && hash.length > 0
        ? ( hash.startsWith( '#' ) ? hash : `#${hash}` )
        : '';

    return `https://${ZAZ_REDIRECT_HOST}${normalizedPath}${queryWithMarker}${normalizedHash}`;
};

/**
 * Consumes (reads and strips) the {@link REDIRECT_SOURCE_PARAM} marker from
 * the current URL. Runs once before React mounts.
 *
 * @param location injectable for tests; defaults to `window.location`.
 * @param historyApi injectable for tests; defaults to `window.history`.
 * @returns `true` if the operator landed here via the DUB -> ZAZ redirect.
 */
export const consumeRedirectMarker = (
    location: Location = window.location,
    historyApi: History = window.history,
): boolean =>
{
    const params = new URLSearchParams( location.search );
    if ( params.get( REDIRECT_SOURCE_PARAM ) !== REDIRECT_SOURCE_VALUE ) return false;

    // Strip the marker so it never survives into bookmarks, shared links, or
    // refreshes (a refresh should not re-show the banner).
    params.delete( REDIRECT_SOURCE_PARAM );
    const remaining = params.toString();
    historyApi.replaceState( null, '', `${location.pathname}${remaining ? `?${remaining}` : ''}${location.hash ?? ''}` );

    return true;
};

/**
 * SPA-bootstrap DUB (EU) -> ZAZ redirect. Runs once before React mounts; on any
 * production EU Midway-door page, performs a full-page redirect to the ZAZ host
 * so the entire session moves to ZAZ.
 *
 * No-ops — returns `false` — when any guard fails:
 *  - AFT-X host (FC Menu owns region routing there),
 *  - non-production env (ZAZ is production-only),
 *  - the operator is not on the EU door (NA / NRT stay; ZAZ is already there).
 *
 * @param location injectable for tests; defaults to `window.location`.
 * @returns `true` if a redirect was triggered (caller should halt bootstrap).
 */
export const runRegionRedirect = ( location: Location = window.location ): boolean =>
{
    // Redirect only the EU (DUB) production Midway door — all three must hold:
    //  - not AFT-X (FC Menu region-routes that door; do not compete),
    //  - production (ZAZ does not exist in dev / beta / gamma),
    //  - region is EU/DUB (leaves NA / NRT, and skips ZAZ itself — loop-safe,
    //    since REGION.ZAZ !== DUB_REGION).
    if ( !isAftxHost( location.host )
        && getEnv( location.host ) === ENV.PRODUCTION
        && getRegion( location.host, ENV.PRODUCTION ) === DUB_REGION )
    {
        location.replace( buildTargetUrl( location.pathname, location.search, location.hash ) );
        return true;
    }

    return false;
};
